import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { OrganizationRoles } from 'src/enums'
import {
  UserAvatarType,
  UserListModel,
  UserListResponse,
} from 'src/models/user.models'
import { connectedPush } from 'src/navigation/Lnk'
import { departmentDetailsUrl, departmentsUrl } from 'src/navigation/urls'
import {
  getDepartmentDetails,
  getDepartments,
} from 'src/queries/organizations/department.network'
import {
  DEPARTMENTS,
  MEMBERS,
  ORG_QK,
  orgQueryKeys,
} from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  useExtendDelete,
  useExtendMutation,
  useExtendSuspenseQuery,
} from 'src/queries/queries.utils'
import { createSetIsAddDeptPopupOpen } from 'src/redux/actions/app.actions'
import { UPDATE_MEMBER_DEPARTMENT } from 'src/redux/actions/data/users.actions'
interface DepartmentMembersResponse {
  departmentMembers: UserListResponse[]
}

export type DepartmentMembersFiltersBody = {
  search?: string
  active?: boolean
  required?: boolean
  count?: number
}

interface QueryDepartmentMembersOptions
  extends ExtendBaseQueryOptions<DepartmentMembersResponse, UserListModel[]> {
  organizationId: string | undefined
  departmentId: string | undefined
}

export const useSuspenseQueryDepartmentMembers = ({
  organizationId,
  departmentId,
  ...rest
}: QueryDepartmentMembersOptions) => {
  return useExtendSuspenseQuery<DepartmentMembersResponse, UserListModel[]>({
    queryKey: orgQueryKeys.departmentMembers(organizationId, departmentId),
    queryUrl: () =>
      `/organizations/${organizationId}/departments/${departmentId}/members`,
    select: (data) =>
      data.departmentMembers
        .map(
          (user) =>
            new UserListModel({ ...user, id: user.userId || user.id || '' }),
        )
        .filter((user) => user !== undefined) as UserListModel[],
    ...rest,
  })
}
export interface DepartmentResponse {
  id: string
  name: string
  description?: string
  code?: string
  size: number
  archived?: boolean
}
export interface DepartmentsResponse {
  departments: DepartmentResponse[]
}

interface QueryDepartmentsOptions
  extends ExtendBaseQueryOptions<DepartmentsResponse, DepartmentResponse[]> {
  organizationId: string | undefined
  includeArchived: boolean
}

export const departmentQueries = {
  list: (organizationId: string | undefined, includeArchived: boolean) =>
    queryOptions({
      queryKey: orgQueryKeys.departments(organizationId, includeArchived),
      queryFn: () => {
        // enabled gates the initial fetch, but refetch()/enabled overrides
        // can still reach queryFn without an organizationId — guard instead
        // of asserting so we fail loudly rather than request
        // `organizations/undefined/departments`.
        if (!organizationId) {
          throw new Error('departmentQueries.list: organizationId is required')
        }
        return getDepartments(organizationId, includeArchived)
      },
      enabled: organizationId !== undefined,
    }),
  detail: (
    organizationId: string | undefined,
    departmentId: string | undefined,
  ) =>
    queryOptions({
      queryKey: orgQueryKeys.departmentDetails(organizationId, departmentId),
      queryFn: () => {
        // enabled gates the initial fetch, but refetch()/enabled overrides
        // can still reach queryFn without these ids — guard instead of
        // asserting so we fail loudly rather than request a malformed URL.
        if (!organizationId || !departmentId) {
          throw new Error(
            'departmentQueries.detail: organizationId and departmentId are required',
          )
        }
        return getDepartmentDetails(organizationId, departmentId)
      },
      enabled: organizationId !== undefined && departmentId !== undefined,
    }),
}

export const useQueryDepartments = ({
  organizationId,
  includeArchived,
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryDepartmentsOptions) => {
  return useQuery({
    ...departmentQueries.list(organizationId, includeArchived),
    // spread overrides replace `enabled`, so re-apply the organizationId guard.
    enabled: enabled && organizationId !== undefined,
    select: (data: DepartmentsResponse) => data.departments,
    ...queryCacheOptions,
    ...rest,
  })
}

interface CreateDepartmentResponse {
  department: DepartmentResponse
}

export const usePostCreateDepartment = (organizationId: string) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  return useExtendMutation<
    CreateDepartmentResponse,
    {
      organizationId: string
      body: { name: string; description?: string; code?: string }
    }
  >(MutationMethod.POST, {
    mutationUrl: (variables) =>
      `/organizations/${variables.organizationId}/departments`,
    onSuccess: (variables) => {
      dispatch(createSetIsAddDeptPopupOpen(false))
      queryClient.invalidateQueries({
        queryKey: [DEPARTMENTS, variables.department.id],
      })
      queryClient.setQueryData(
        [ORG_QK, organizationId, DEPARTMENTS],
        (data: DepartmentsResponse) => {
          return {
            departments: [
              ...data.departments,
              { ...variables.department, size: 0 },
            ],
          }
        },
      )
      dispatch(
        connectedPush(
          departmentDetailsUrl(organizationId, variables.department.id),
        ),
      )
    },
  })
}

export type UpdateDepartmentBody = {
  name: string
  description?: string
  code?: string
}

export const usePutUpdateDepartment = (
  organizationId: string,
  departmentId: string,
) => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    DepartmentDetailsResponse,
    { organizationId: string; departmentId: string; body: UpdateDepartmentBody }
  >(MutationMethod.PUT, {
    mutationUrl: (variables) =>
      `/organizations/${variables.organizationId}/departments/${variables.departmentId}`,
    onSuccess: (variables) => {
      queryClient.invalidateQueries({
        queryKey: [ORG_QK, organizationId, DEPARTMENTS, departmentId],
      })
      queryClient.setQueryData(
        [ORG_QK, organizationId, DEPARTMENTS, departmentId],
        { department: variables },
      )
    },
  })
}

export const useDeleteDepartment = (
  organizationId: string,
  departmentId: string,
) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  return useExtendDelete<
    { msg: string },
    { organizationId: string; departmentId: string }
  >({
    mutationUrl: () =>
      `/organizations/${organizationId}/departments/${departmentId}`,
    onSuccess: () => {
      queryClient.setQueryData<DepartmentsResponse | undefined>(
        orgQueryKeys.departments(organizationId, false),
        (data) => {
          if (data === undefined) {
            return data
          }

          return {
            ...data,
            departments: data.departments.filter(
              (department) => department.id !== departmentId,
            ),
          }
        },
      )
      queryClient.setQueryData<DepartmentsResponse | undefined>(
        orgQueryKeys.departments(organizationId, true),
        (data) => {
          if (data === undefined) {
            return data
          }

          return {
            ...data,
            departments: data.departments.map((department) =>
              department.id === departmentId
                ? { ...department, archived: true, size: 0 }
                : department,
            ),
          }
        },
      )
      queryClient.removeQueries({
        queryKey: orgQueryKeys.departmentDetails(organizationId, departmentId),
        exact: true,
      })
      queryClient.removeQueries({
        queryKey: orgQueryKeys.departmentMembers(organizationId, departmentId),
        exact: true,
      })
      dispatch(
        connectedPush({
          pathname: departmentsUrl(organizationId),
          search: '',
          hash: '',
          state: {},
        }),
      )
    },
  })
}

export interface DepartmentDetailsResponse {
  id: string
  name: string
  description?: string
  code?: string
}
interface DepartmentFullResponse {
  department: DepartmentDetailsResponse
}

interface QueryDepartmentDetailsOptions
  extends ExtendBaseQueryOptions<
    DepartmentFullResponse,
    DepartmentDetailsResponse
  > {
  organizationId: string
  departmentId: string
}

export const useQueryDepartmentDetails = ({
  organizationId,
  departmentId,
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryDepartmentDetailsOptions) => {
  return useQuery({
    ...departmentQueries.detail(organizationId, departmentId),
    // spread overrides replace `enabled`, so re-apply the presence guards.
    enabled:
      enabled && organizationId !== undefined && departmentId !== undefined,
    select: (data: DepartmentFullResponse) => data.department,
    ...queryCacheOptions,
    ...rest,
  })
}

export const useSuspenseQueryDepartmentDetails = ({
  organizationId,
  departmentId,
  ...rest
}: QueryDepartmentDetailsOptions) => {
  return useExtendSuspenseQuery<
    DepartmentFullResponse,
    DepartmentDetailsResponse
  >({
    queryKey: orgQueryKeys.departmentDetails(organizationId, departmentId),
    queryUrl: () =>
      `/organizations/${organizationId}/departments/${departmentId}`,
    select: (data: DepartmentFullResponse) => data.department,
    ...rest,
  })
}

interface departmentMember {
  userId: string
  firstName: string
  lastName: string
  email: string
  avatarType: UserAvatarType
  avatarUrl: string
  organizationRole: OrganizationRoles
}

interface AddMemberToDepartmentResponse {
  departmentMembers: departmentMember[]
}

interface AddDepartmentMembersBody {
  userIds: string[]
}

// only adds a department; fails if user is already in a department
export const useQueryAddDepartmentMember = (
  organizationId: string,
  departmentId: string,
  departmentName: string,
  closeEditingState: () => void,
) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  return useExtendMutation<
    AddMemberToDepartmentResponse,
    { body: AddDepartmentMembersBody }
  >(MutationMethod.POST, {
    mutationUrl: () =>
      `/organizations/${organizationId}/departments/${departmentId}/members`,
    onSuccess: (variables) => {
      // update department members list
      queryClient.setQueryData(
        [ORG_QK, organizationId, DEPARTMENTS, departmentId, MEMBERS],
        (data: DepartmentMembersResponse) => {
          return {
            departmentMembers: [
              ...(data?.departmentMembers ?? []),
              ...(variables.departmentMembers ?? []),
            ],
          }
        },
      )
      // update member user object
      dispatch({
        type: UPDATE_MEMBER_DEPARTMENT,
        payload: {
          userIds: variables.departmentMembers,
          department: { id: departmentId, name: departmentName },
        },
      })
      closeEditingState()
    },
  })
}

// deletes any old department associated then adds new department
export const useQueryForceAddDepartmentMember = (organizationId: string) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  return useExtendMutation<
    AddMemberToDepartmentResponse,
    {
      departmentId: string
      departmentName: string
      body: AddDepartmentMembersBody
    }
  >(MutationMethod.PATCH, {
    mutationUrl: (variables) =>
      `/organizations/${organizationId}/departments/${variables.departmentId}/members`,
    onSuccess: (_, variables) => {
      // update department members list
      queryClient.setQueryData(
        [ORG_QK, organizationId, DEPARTMENTS, variables.departmentId, MEMBERS],
        (data: DepartmentMembersResponse) => {
          return {
            departmentMembers: [
              ...(data?.departmentMembers ?? []),
              ...(variables.body.userIds ?? []),
            ],
          }
        },
      )
      // update member user object
      dispatch({
        type: UPDATE_MEMBER_DEPARTMENT,
        payload: {
          userIds: variables.body.userIds,
          department: {
            id: variables.departmentId,
            name: variables.departmentName,
          },
        },
      })
    },
  })
}

export const useDeleteDepartmentMember = (
  organizationId: string,
  departmentId: string,
  userId: string,
) => {
  const queryClient = useQueryClient()
  return useExtendDelete<
    { msg: string },
    { organizationId: string; departmentId: string; userId: string }
  >({
    mutationUrl: () =>
      `/organizations/${organizationId}/departments/${departmentId}/members/${userId}`,
    onSuccess: () => {
      queryClient.setQueryData(
        [ORG_QK, organizationId, DEPARTMENTS, departmentId, MEMBERS],
        (data: DepartmentMembersResponse) => {
          return {
            departmentMembers: data.departmentMembers.filter(
              (member) => member.userId !== userId,
            ),
          }
        },
      )
      queryClient.removeQueries({
        queryKey: [
          ORG_QK,
          organizationId,
          DEPARTMENTS,
          departmentId,
          MEMBERS,
          userId,
        ],
      })
    },
  })
}
